import { useMemo, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { ChevronRight } from "lucide-react"
import { sortByUrgency } from "@/lib/my-work-sort"
import { daysUntilDeadline, deadlineTimeLabel, isActiveBidStatus, isDeadlinePast, withDeadlineTime } from "@/lib/bid-deadline"
import { shortDate } from "@/lib/datetime"
import { cn } from "@/lib/utils"
import { WorkItemRow } from "@/components/my-work/WorkItemRow"
import { CommentWorkRow } from "@/components/my-work/CommentWorkRow"
import { NeedsAttentionRow } from "@/components/dashboard/NeedsAttentionRow"
import { BoxedList, SECTION_LINK_CLASS } from "@/components/dashboard/DashboardSection"
import { DUE_SOON_DAYS } from "@/components/dashboard/deadlines"
import { countMyWork } from "@/lib/my-work-counts"
import type { MyWorkCommentItem, MyWorkItem } from "@/stores/my-work-store"
import type { Bid } from "@/lib/schemas"

interface NeedsYouProps {
  items: MyWorkItem[]
  comments: MyWorkCommentItem[]
  bids: Bid[]
  /** The bids list is still loading, so the deadline counts would read 0. */
  deadlinesLoading?: boolean
  /** Injectable clock; defaults to the current time. */
  now?: Date
}

/** Rows shown on Home; the full queue is on /app/my-work. */
export const NEEDS_YOU_ROW_CAP = 6
/** Slots kept for comment threads, so a long section queue can't hide a waiting reply. */
const COMMENT_SLOTS = 2

function DueChip({ bid, now }: { bid: Bid | undefined; now: Date }) {
  if (!bid?.deadline || bid.archived || !isActiveBidStatus(bid.status)) return null
  const days = daysUntilDeadline(bid.deadline, now)
  if (days === null) return null
  if (isDeadlinePast(bid, now)) {
    return (
      <span className="shrink-0 whitespace-nowrap text-[11px] text-destructive">
        {withDeadlineTime(shortDate(bid.deadline), bid)} · {days < 0 ? `${Math.abs(days)}d overdue` : "overdue"}
      </span>
    )
  }
  if (days <= DUE_SOON_DAYS) {
    const time = deadlineTimeLabel(bid)
    return (
      <span className="shrink-0 whitespace-nowrap text-[11px] text-[hsl(38_85%_35%)]">
        {days === 0 ? `Due today${time ? `, ${time}` : ""}` : `Due in ${days}d`}
      </span>
    )
  }
  return (
    <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-foreground">
      {withDeadlineTime(shortDate(bid.deadline), bid)}
    </span>
  )
}

interface Count {
  key: string
  label: string
  href: string
  danger?: boolean
}

// The header's one quiet line of counts (was the four GlanceBar tiles). Zero
// counts are left out; each count deep-links into My Work with its filter.
function CountsLine({ counts }: { counts: Count[] }) {
  if (counts.length === 0) return null
  const parts: ReactNode[] = []
  counts.forEach((c, i) => {
    if (i > 0) parts.push(<span key={`sep-${c.key}`} aria-hidden> · </span>)
    parts.push(
      <Link
        key={c.key}
        to={c.href}
        data-testid={`needs-you-count-${c.key}`}
        className={cn("hover:text-foreground hover:underline", c.danger && "text-destructive hover:text-destructive")}
      >
        {c.label}
      </Link>,
    )
  })
  return <p className="text-meta" data-testid="needs-you-counts">{parts}</p>
}

// Home's "Needs you": the user's most urgent work — assigned sections, review
// requests and comment threads waiting on them — then a quieter "Worth a look"
// group of insights. Sections lead, by deadline; up to COMMENT_SLOTS comment
// threads (newest first) take the last rows, or more when sections run short.
export function NeedsYou({ items, comments, bids, deadlinesLoading = false, now = new Date() }: NeedsYouProps) {
  const bidsById = useMemo(() => new Map(bids.map((b) => [b.id, b])), [bids])

  const { sectionRows, commentRows } = useMemo(() => {
    const sorted = sortByUrgency(items, bidsById)
    const newestComments = [...comments].sort((a, b) => b.at.localeCompare(a.at))
    const reserved = Math.min(newestComments.length, COMMENT_SLOTS)
    const sectionRows = sorted.slice(0, NEEDS_YOU_ROW_CAP - reserved)
    const commentRows = newestComments.slice(0, NEEDS_YOU_ROW_CAP - sectionRows.length)
    return { sectionRows, commentRows }
  }, [items, comments, bidsById])

  // The My Work page's own definitions (countMyWork), so each count matches
  // the page its link opens: sections, not bids.
  const counts = useMemo(() => {
    const { assigned, review, overdue, closingSoon: dueSoon } = countMyWork(items, bidsById, now)
    const list: Count[] = []
    if (assigned > 0) list.push({ key: "assigned", label: `${assigned} assigned`, href: "/app/my-work" })
    if (review > 0) list.push({ key: "review", label: `${review} to review`, href: "/app/my-work?tab=review" })
    if (!deadlinesLoading && dueSoon > 0) {
      list.push({ key: "closing", label: `${dueSoon} due soon`, href: "/app/my-work?filter=closing" })
    }
    if (!deadlinesLoading && overdue > 0) {
      list.push({ key: "overdue", label: `${overdue} overdue`, href: "/app/my-work?filter=overdue", danger: true })
    }
    return list
  }, [items, bidsById, deadlinesLoading, now])

  const isEmpty = sectionRows.length === 0 && commentRows.length === 0

  return (
    <section data-testid="needs-you" aria-labelledby="needs-you-title">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h2 id="needs-you-title" className="text-sm font-medium">Needs you</h2>
          <CountsLine counts={counts} />
        </div>
        <Link to="/app/my-work" className={cn(SECTION_LINK_CLASS, "shrink-0")} data-testid="needs-you-see-all">
          See all
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </Link>
      </div>
      {isEmpty ? (
        <p className="text-[13px] text-muted-foreground" data-testid="needs-you-empty">
          Nothing needs you right now.
        </p>
      ) : (
        <BoxedList>
          {sectionRows.map((item) => (
            <WorkItemRow
              key={item.id}
              item={item}
              showBid
              aside={<DueChip bid={bidsById.get(item.bidId)} now={now} />}
              testId="needs-you-row"
            />
          ))}
          {commentRows.map((item) => (
            <CommentWorkRow key={item.commentId} item={item} />
          ))}
        </BoxedList>
      )}
      <div className="mt-4 empty:hidden">
        <NeedsAttentionRow />
      </div>
    </section>
  )
}
